import { FC, useEffect, useMemo } from "react"
import React from "react"

import { DialogContent, DialogTitle, Grid, SxProps, Typography } from "../../../../../ui-component/mui.components"

import { BootstrapDialog } from "./ExistingPatientCheckModal.style"
import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import { RootState } from "@/core/app/store"
import PatientInfoHeader from "../PatientInfoHeader"
import { fetchPatientById } from "@/core/app/slices/patients"
import { getCaseByPatient } from "@/core/app/slices/case"
import { UButton } from "@/ui-component"
import RenderByCaseStatus from "../../RenderByCaseStatus"
import { useTranslation } from "react-i18next"
import { setSelectedCaseIdInModal } from "@/core/app/slices/patients/patientSlice"

/** Which flow is showing this modal — determines title, description, and which actions are available */
export type ExistingPatientCheckModalVariant = "newPatient" | "backstop"

export interface ExistingPatientCheckModalBaseProps {
  isOpen: boolean
  patient: {
    firstName: string
    id: number
    lastName: string
    orgId: number
  } | null
  setClose: () => void
  sxModalProps?: SxProps
}

export interface NewPatientModalProps extends ExistingPatientCheckModalBaseProps {
  variant: "newPatient"
  actions: { createNewCase: () => void }
}

export interface BackstopModalProps extends ExistingPatientCheckModalBaseProps {
  variant: "backstop"
}

/** Props for ExistingPatientCheckModal. Use `variant` to indicate context; pass only the `actions` for that variant. */
export type ImodalProps = NewPatientModalProps | BackstopModalProps

const ExistingPatientCheckModal: FC<ImodalProps> = ({
  isOpen,
  patient,
  setClose,
  sxModalProps,
  variant,
  actions,
}) => {
  const isNewPatient = variant === "newPatient"
  const { caseList } = useAppSelector((state: RootState) => state.caseService)
  const { patientData } = useAppSelector(
    (state: RootState) => state.patientService,
  )
  const { t } = useTranslation("common")
  const dispatch = useAppDispatch()

  const { selectedCaseIdInModal } = useAppSelector((state: RootState) => state.patientService)

  useEffect(()=>{
    if(!isOpen){
      dispatch(setSelectedCaseIdInModal(null))
    }
  },[isOpen, dispatch])

  const currentCaseObj = useMemo(()=>{
    return caseList.find((caseObj)=>String(caseObj.id)===selectedCaseIdInModal)
  },[caseList, selectedCaseIdInModal])

  useEffect(() => {
    if(patient && isOpen){
      const patientId = patient?.id.toString()
      dispatch(getCaseByPatient({ patientId: patientId, isCaseArchived: true }))
      dispatch(fetchPatientById({ patientId }))
    }
  }, [patient, isOpen])
    
  return (
    <BootstrapDialog open={isOpen} sx={{ ...sxModalProps }}>
      <DialogTitle id="titleCntr">
        <Grid display={"flex"} flexDirection={"column"}>
          <Typography variant="h5">
            {isNewPatient ? t("existingPatientModal.title") : t("backstop.reviewLabel")}
          </Typography>
          <Typography variant="body1">
            {isNewPatient ? t("existingPatientModal.description") : ""}
          </Typography>
        </Grid>
      </DialogTitle>
      <DialogContent id="ExistingPatientCheckModal-contentCntr">
        <Grid>
          <PatientInfoHeader
            status={currentCaseObj?.caseDisposition}
            patientData={patientData}
            caseDetail={!!currentCaseObj && currentCaseObj}
          />
        </Grid>
        <Grid>
          <RenderByCaseStatus />
        </Grid>
      </DialogContent>
      <Grid
        id="btnListCntr"
        container
        flexDirection={"row"}
        justifyContent={"space-between"}
      >
        <Grid flexDirection={"row"} justifyContent={"flex-start"}>
          <UButton
            variant="outlined"
            btnText={t("button.back")}
            sxProp={{
              minWidth: 58,
              height: 36,
            }}
            onClickHandler={setClose}
          ></UButton>
        </Grid>
        {isNewPatient && actions && (
          <Grid flexDirection={"row"} justifyContent={"flex-end"}>
            <UButton
              variant="shade"
              btnText={t("existingPatientModal.createNewPatient")}
              sxProp={{
                minWidth: 58,
                height: 36,
                marginRight: 1,
              }}
              onClickHandler={setClose}
            ></UButton>
            <UButton
              variant="contained"
              btnText={t("existingPatientModal.createNewCase")}
              sxProp={{
                minWidth: 54,
                height: 36,
                boxShadow:
                  "0px 1px 5px rgba(0, 0, 0, 0.12), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.2)",
              }}
              onClickHandler={actions.createNewCase}
            ></UButton>
          </Grid>
        )}
      </Grid>
    </BootstrapDialog>
  )
}

export default ExistingPatientCheckModal
